<template>
	<sk-dialog
		persistent
		:model-value="modelValue"
		:title="$t('billing.activity-report')"
		:icon="$i('chart-mixed')"
		@update:model-value="updateModelValue"
	>
		<simple-form :id="FORM_ID" class="w-100" @submit.prevent="generateReport" :disabled="billingActivityReportLoading">
			<v-row>
				<v-col>
					<sk-select v-model="startPeriodValue" v-bind="startPeriodProps" :items="startPeriodItems" />
				</v-col>
				<v-col>
					<sk-select v-model="endPeriodValue" v-bind="endPeriodProps" :items="endPeriodItems" />
				</v-col>
			</v-row>

			<sk-select class="mt-3" v-model="typesValue" v-bind="typesProps" />
		</simple-form>
		<template #footer="{ close }">
			<cancel-btn @click="close" />
			<sk-primary-btn type="submit" :form="FORM_ID" :loading="billingActivityReportLoading" :text="$t('commons.cta.generate')" />
		</template>
	</sk-dialog>
</template>

<style scoped lang="scss"></style>

<script setup lang="ts">
import { saveAs } from 'file-saver';
import { kebabCase } from 'lodash-es';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { finalize, tap } from 'rxjs/operators';
import { useI18n } from 'vue-i18n';
import { VCol, VRow } from 'vuetify/components';

import { SkPrimaryBtn } from '@silae/components/buttons';
import { SkDialog } from '@silae/components/feedback';
import { SkSelect } from '@silae/components/inputs';
import { useEventPropagation } from '@silae/composables/component';
import { useSubscriptor } from '@silae/composables/subscriptor';
import { SimpleForm } from '~/components/shared';
import { useLoading, useNotification } from '~/composables';
import { MimeType } from '~/domain/enums';
import { generateBillingReport } from '~/services';
import { useAuthStore } from '~/stores';

import { CancelBtn } from '../../shared/buttons';
import { useBillingActivityReportForm } from './composables';

const FORM_ID = 'BILLING_ACTIVITY_REPORT_FORM';
const LOADING_FLAG_ACTIVITY_REPORT = 'billingActivityReportLoading';

/** Props and emits */
defineProps<{ modelValue: boolean }>();
defineEmits(['update:model-value']);

/** Composables and computed */
const { trigger } = useSubscriptor();
const { t } = useI18n();
const authStore = useAuthStore();
const { userPartnerLabel } = storeToRefs(authStore);
const { updateModelValue } = useEventPropagation(['update:model-value']);
const { billingActivityReportLoading, startLoading, stopLoading } = useLoading([LOADING_FLAG_ACTIVITY_REPORT]);
const { notifyResult } = useNotification();

const {
	handleSubmit,
	resetForm,
	startPeriodValue,
	startPeriodProps,
	startPeriodItems,
	endPeriodValue,
	endPeriodProps,
	endPeriodItems,
	typesValue,
	typesProps
} = useBillingActivityReportForm(billingActivityReportLoading);

/** Functions used in comp */
const generateReport = handleSubmit(({ startPeriod, endPeriod, types }) => {
	startLoading(LOADING_FLAG_ACTIVITY_REPORT);
	trigger(
		generateBillingReport(startPeriod, endPeriod, types).pipe(
			notifyResult(t('notifications.success.action'), t('notifications.error.action')),
			tap(fileBits => {
				const now = DateTime.now();
				const formattedNow = now.toISODate();
				const file = new File([fileBits], `${kebabCase(userPartnerLabel.value)}_${formattedNow}_${now.toMillis()}`, {
					type: MimeType.ZIP
				});
				saveAs(file);
			}),
			finalize(() => {
				stopLoading(LOADING_FLAG_ACTIVITY_REPORT);
				// reset form
				resetForm();
				// and close dialog
				updateModelValue(false);
			})
		)
	);
});
</script>
